import {
  Box, FormControl, Grid, Grid2, IconButton, MenuItem,
  Popover, Select, TextField, Typography, useMediaQuery, useTheme,
} from "@mui/material";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircle";
import React, { useEffect, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useDispatch, useSelector } from "react-redux";
import {
  checkGuestLimit, setFreeTablesData, setGuestCount, setIsBackToHoreca,
  setReservationGroupDatas, setReservationTypeDatas, updateReservationGroup,
  updateReservationType, updateSelectedDate, updateTime, updateTotalCount,
} from "../../store/slice/Addguest.slice";
import { updateBlockedDates } from "../../store/slice/widgetSetting.slice";
import { ClearIcon } from "@mui/x-date-pickers/icons";
import { useWidgetHeight } from "./widgetHeightContext";

const GUEST_FIELDS = [
  { fieldKey: "field1Text", guestType: "adult"   },
  { fieldKey: "field2Text", guestType: "child"   },
  { fieldKey: "field3Text", guestType: "kid"     },
  { fieldKey: "field4Text", guestType: "baby"    },
  { fieldKey: "field5Text", guestType: "toddler" },
];

// ─── Stepper (> 10 guests) ────────────────────────────────────────────────────
const GuestStepper = ({ count, guestType, guestsCss, theme, onDecrement, onIncrement }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "0.1rem", sm: "0.2rem" } }}>
    <Typography
      sx={{ fontSize: { xs: "18px", sm: "24px" }, color: guestsCss?.incrementIconClr ?? theme?.guest?.textColor, cursor: "pointer" }}
      onClick={onDecrement}
    >-</Typography>
    <TextField
      sx={{
        height: "30px",
        "& .MuiOutlinedInput-root": {
          "& fieldset": { borderColor: theme?.guest?.dropdownBorderColor },
          "&.Mui-focused fieldset": { borderColor: theme?.guest?.dropdownBorderColor },
        },
        "& .MuiOutlinedInput-input": { padding: 1, color: theme?.guest?.textColor ?? "#9F9F9F" },
      }}
      size="small" placeholder="00" name={guestType} value={count} disabled
    />
    <Typography
      sx={{ fontSize: { xs: "18px", sm: "24px" }, color: guestsCss?.incrementIconClr ?? theme?.guest?.textColor, cursor: "pointer" }}
      onClick={onIncrement}
    >+</Typography>
  </Box>
);

// ─── Dropdown (1–10 + "more") ─────────────────────────────────────────────────
const GuestDropdown = ({ value, guestType, guestsCss, theme, onChange, onMoreThan10 }) => (
  <Select
    value={value || ""}
    size="small"
    fullWidth
    IconComponent={KeyboardArrowDownIcon}
    sx={{
      width: "100%",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: theme?.guest?.dropdownBorderColor },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: theme?.guest?.dropdownBorderColor },
      "& .MuiOutlinedInput-input": { padding: 1, color: theme?.guest?.textColor, fontSize: { sm: "12px" } },
    }}
    onChange={(e) => onChange(Number(e.target.value))}
  >
    {Array.from({ length: 10 }, (_, i) => (
      <MenuItem
        key={i} value={i + 1}
        sx={{
          color: theme?.guest?.dropdownTextColor, fontSize: { sm: "12px" },
          "&:hover": { backgroundColor: theme?.guest?.dropdownBgHover, color: theme?.guest?.dropdownTextColorHover },
        }}
      >{i + 1}</MenuItem>
    ))}
    <MenuItem
      value={11}
      sx={{ color: theme?.guest?.dropdownTextColor, fontSize: { sm: "12px" } }}
      onClick={(e) => { e.stopPropagation(); onMoreThan10(); }}
    >
      <AddCircleOutlineIcon sx={{ color: theme?.guest?.plusColor, width: { xs: 16, sm: 20 }, height: { xs: 16, sm: 20 } }} />
    </MenuItem>
  </Select>
);

// ─── Single guest row ─────────────────────────────────────────────────────────
const GuestRow = ({ label, guestType, count, isOver10, guestsCss, theme, isSmallScreen, dispatch, resetReservationData }) => {
  const handleClear      = () => dispatch(setGuestCount({ guestType, count: 0 }));
  const handleDecrement  = () => { if (count > 1) { resetReservationData(); dispatch(setGuestCount({ guestType, count: count - 1 })); } };
  const handleIncrement  = () => { resetReservationData(); dispatch(setGuestCount({ guestType, count: count + 1 })); };
  const handleDropdown   = (val) => { resetReservationData(); dispatch(setGuestCount({ guestType, count: val })); };
  const handleMoreThan10 = () => { resetReservationData(); dispatch(checkGuestLimit({ guestType, isGreaterThan10: true })); dispatch(setGuestCount({ guestType, count: 11 })); };

  return (
    <Grid2 container sx={{ display: "flex", alignItems: "center", mb: 2 }} spacing={2}>
      <Grid2 size={{ xs: 8 }} sx={{ textAlign: "left" }}>
        <Typography sx={{ fontSize: { sm: "12px" }, color: guestsCss?.GuestTextColor, fontWeight: "bold", ml: 1 }}>
          {label}
        </Typography>
      </Grid2>
      <Grid2 size={{ xs: 4 }}>
        <FormControl fullWidth sx={{ width: "100%", height: "32px", px: 1, position: "relative" }}>
          {count > 0 && (
            <IconButton
              sx={{ position: "absolute", left: isSmallScreen ? -20 : -30, top: "52%", transform: "translateY(-50%)", width: "30px", height: "30px", "&:hover": { backgroundColor: "transparent" } }}
              onClick={handleClear}
            >
              <ClearIcon sx={{ width: "100%", color: guestsCss?.clearIcon }} />
            </IconButton>
          )}
          {isOver10
            ? <GuestStepper count={count} guestType={guestType} guestsCss={guestsCss} theme={theme} onDecrement={handleDecrement} onIncrement={handleIncrement} />
            : <GuestDropdown value={count} guestType={guestType} guestsCss={guestsCss} theme={theme} onChange={handleDropdown} onMoreThan10={handleMoreThan10} />
          }
        </FormControl>
      </Grid2>
    </Grid2>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const Addguest = ({ isOpen, handleClose, Ref }) => {
  const { css }       = useSelector((store) => store?.widgetSettings);
  const guestsCss     = css?.find((item) => item?.cssTabFieldsGroupName === "Guests")?.fields;
  const theme         = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // ← only onPopoverClose needed here (for backdrop/outside clicks)
  // onPopoverOpen is called from Widget.jsx's toggle before this even mounts
  const { onPopoverClose } = useWidgetHeight();

  const [anchorOriginVert,    setAnchorOriginVert]    = useState("bottom");
  const [transformOriginVert, setTransformOriginVert] = useState("top");

  const {
    adult, baby, child, kid, toddler,
    isGreaterThan10, languageWords, selectedDate,
    reservType, time, reservGroup, freeTablesData,
    reservationTypeDatas, reservationGroupData,
  } = useSelector((store) => store.addGuest);

  const { widgetConfig, blockedDates } = useSelector((store) => store?.widgetSettings);
  const dispatch = useDispatch();

  const guestCounts    = { adult, child, kid, baby, toddler };
  const nonEmptyFields = Object.keys(widgetConfig?.fields || {})
    .filter((key) => key.startsWith("field") && widgetConfig?.fields[key]?.trim() !== "")
    .map((key) => widgetConfig.fields[key]);

  useEffect(() => {
    const total = [adult, baby, child, toddler, kid].reduce((sum, v) => sum + (Number(v) || 0), 0);
    dispatch(updateTotalCount(total));
  }, [adult, baby, child, kid, toddler]);

  useEffect(() => {
    ["adult", "child", "kid", "baby", "toddler"].forEach((type) => {
      dispatch(checkGuestLimit({ guestType: type, isGreaterThan10: guestCounts[type] > 10 }));
    });
  }, [adult, child, kid, baby, toddler]);

  useEffect(() => {
    if (!Ref?.current) return;
    const rect = Ref.current.getBoundingClientRect();
    const nearBottom = rect.bottom + 400 > window.innerHeight;
    setAnchorOriginVert(nearBottom ? "top" : "bottom");
    setTransformOriginVert(nearBottom ? "bottom" : "top");
  }, [isOpen, Ref]);

  const resetReservationData = () => {
    dispatch(setIsBackToHoreca(false));
    if (blockedDates?.length > 0)             dispatch(updateBlockedDates([]));
    if (reservationTypeDatas?.length > 0)     dispatch(setReservationTypeDatas([]));
    if (reservationGroupData?.length > 0)     dispatch(setReservationGroupDatas([]));
    if (freeTablesData?.length > 0)           dispatch(setFreeTablesData([]));
    if (Object.keys(reservType || {}).length > 0) dispatch(updateReservationType({}));
    if (selectedDate?.trim() !== "")          dispatch(updateSelectedDate(""));
    if (time?.trim() !== "")                  dispatch(updateTime(""));
    if (reservGroup && Object.keys(reservGroup).length > 0) dispatch(updateReservationGroup({}));
  };

  // ── Shared popover props ───────────────────────────────────────────────────
  // onClose handles the case where user clicks the backdrop/outside to dismiss.
  // Widget.jsx's close() already calls onPopoverClose for button-triggered closes,
  // but this covers the MUI backdrop dismiss path.
  const popoverCommonProps = {
    open:            isOpen,
    anchorEl:        Ref?.current,
    onClose:         () => { handleClose(); onPopoverClose(); },
    anchorOrigin:    { vertical: anchorOriginVert,    horizontal: "center" },
    transformOrigin: { vertical: transformOriginVert, horizontal: "center" },
    disableAutoFocus:    true,
    disableEnforceFocus: true,
    disableRestoreFocus: true,
  };

  // ── Single-field mode (grid picker) ──────────────────────────────────────
  if (!nonEmptyFields || nonEmptyFields.length <= 1) {
    return (
      <Popover
        {...popoverCommonProps}
        PaperProps={{
          sx: {
            backgroundColor: guestsCss?.GuestBackgroundColor,
            width: 200, borderRadius: 2, boxShadow: 6, p: 2, maxHeight: 600,
            overflow: "hidden", overflowY: "scroll",
            scrollbarWidth: "none", msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          },
          onWheel: (e) => e.stopPropagation(),
        }}
      >
        <Grid item xs={12}>
          <Typography sx={{ fontSize: "13px", mb: 1, color: guestsCss?.GuestHeaderTextColor }}>
            {languageWords?.Guest}
          </Typography>

          {isGreaterThan10?.adult ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: `1px solid ${guestsCss?.GuestDropDownBorderColor}`, borderRadius: 1, px: 2, py: 1, mb: 2 }}>
              <IconButton onClick={() => { if (adult > 1) { resetReservationData(); dispatch(setGuestCount({ guestType: "adult", count: adult - 1 })); } }}>
                <RemoveCircleIcon sx={{ color: guestsCss?.decrementColorIcon }} />
              </IconButton>
              <Typography fontSize={16}>{adult}</Typography>
              <IconButton onClick={() => { resetReservationData(); dispatch(setGuestCount({ guestType: "adult", count: adult + 1 })); }}>
                <AddCircleOutlineIcon sx={{ color: guestsCss?.incrementIconClr }} />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 320, overflowY: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", width: "100%", gap: 1 }}>
                {Array.from({ length: 9 }, (_, i) => {
                  const value    = i + 1;
                  const isActive = adult === value;
                  return (
                    <Box
                      key={value}
                      onClick={() => {
                        resetReservationData();
                        dispatch(setGuestCount({ guestType: "adult", count: value }));
                        handleClose();
                        onPopoverClose(); // snap height back
                      }}
                      sx={{
                        height: 44, px: 2, mb: 0.5,
                        border: `1px dashed ${guestsCss?.GuestDropDownBorderColor}`,
                        borderRadius: 1.5, display: "flex", justifyContent: "center", alignItems: "center",
                        cursor: "pointer", transition: "all 0.2s ease",
                        backgroundColor: isActive ? `#${guestsCss?.selectGuestBg}` : guestsCss?.GuestDropDownBackgroundColor,
                        color: isActive ? `#${guestsCss?.selectGuestTxtClr}` : guestsCss?.GuestDropDownTextColor,
                        "&:hover": { backgroundColor: guestsCss?.GuestDropDownBackgroundColorHover, color: guestsCss?.GuestDropDownTextColorHover },
                      }}
                    >
                      <Typography fontSize={18}>{value}</Typography>
                    </Box>
                  );
                })}

                <Box
                  onClick={() => {
                    dispatch(checkGuestLimit({ guestType: "adult", isGreaterThan10: true }));
                    dispatch(setGuestCount({ guestType: "adult", count: 11 }));
                    handleClose();
                    onPopoverClose();
                  }}
                  sx={{ border: `1px dashed ${guestsCss?.GuestDropDownBorderColor}`, px: 2, borderRadius: 1.5, display: "flex", justifyContent: "center", alignItems: "center", gap: 1, cursor: "pointer", transition: "0.2s" }}
                >
                  <AddCircleOutlineIcon sx={{ color: guestsCss?.plusColor, "&:hover": { color: guestsCss?.plusHover } }} fontSize="large" />
                </Box>
              </Box>
            </Box>
          )}
        </Grid>
      </Popover>
    );
  }

  // ── Multi-field mode ──────────────────────────────────────────────────────
  return (
    <Popover
      {...popoverCommonProps}
      PaperProps={{
        sx: {
          backgroundColor: guestsCss?.GuestBackgroundColor,
          width: isSmallScreen ? "100%" : "320px", maxWidth: "430px",
          borderRadius: 2, boxShadow: 3, p: 2,
          overflowY: "scroll", scrollbarWidth: "none", msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        },
      }}
    >
      {widgetConfig?.fields != null ? (
        <Box sx={{ width: "100%", height: "auto", pt: 2 }}>
          <Grid2 container spacing={2} sx={{ width: "100%", mb: 1 }}>
            <Grid2 size={{ xs: 8 }}>
              <Typography sx={{ fontSize: "16px", color: guestsCss?.GuestHeaderTextColor, fontWeight: "600", borderRadius: "8px", p: 1 }}>
                {languageWords?.Guest}
              </Typography>
            </Grid2>
            <Grid2 size={{ xs: 4 }}>
              <Typography sx={{ fontSize: "16px", color: guestsCss?.GuestHeaderTextColor, fontWeight: "600", textAlign: "center", backgroundColor: theme?.guest?.bgColor, borderRadius: "8px", p: 1 }}>
                {languageWords?.quantity}
              </Typography>
            </Grid2>
          </Grid2>
          <Box>
            {GUEST_FIELDS.map(({ fieldKey, guestType }) => {
              const label = widgetConfig?.fields?.[fieldKey]?.trim();
              if (!label) return null;
              return (
                <GuestRow
                  key={guestType}
                  label={label}
                  guestType={guestType}
                  count={guestCounts[guestType]}
                  isOver10={isGreaterThan10?.[guestType]}
                  guestsCss={guestsCss}
                  theme={theme}
                  isSmallScreen={isSmallScreen}
                  dispatch={dispatch}
                  resetReservationData={resetReservationData}
                />
              );
            })}
          </Box>
        </Box>
      ) : (
        <Box sx={{ backgroundColor: theme?.guest?.bgColor, width: "230px", height: "321px", pt: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Typography sx={{ fontSize: { xs: "14px", sm: "18px" } }}>No fields found</Typography>
        </Box>
      )}
    </Popover>
  );
};

export default Addguest;